<template>
    <tbody class="crb-row" :class="{'is-origin': origin, 'has-comment': show_comment_row}" :data-bank="bank.slug">
        <tr class="crb-row__main">
            <td>
                <div class="crb-bank">
                    <img class="crb-bank__logo" :class="{'rounded-circle': bank_logo_round}" :src="bank_logo" alt="" width="26" height="26" aria-hidden="true">
                    <span class="crb-bank__name">{{bank.name}}</span>
                </div>
            </td>
            <template v-if="credit_request">
                <td>
                    <CreditPicker v-model="credit_manager_id" :columns="managers_columns" :title="bank.name + ' · Ответственный'" :aria-label="bank.name + ' — Ответственный'"
                                  placeholder="Не назначен" :current-label="current_manager_name" :width="260" :has-error="form.errors.has('credit_manager_id')"
                                  :disabled="!$hasPermission('credit_requests.edit')" @change="validate() && save()" />
                    <has-error :form="form" field="credit_manager_id"/>
                </td>
                <td>
                    <CreditRequestStatusPicker v-model="status" :options="status_options" :title="bank.name + ' · Статус'" :aria-label="bank.name + ' — Статус'"
                                               :has-error="form.errors.has('status')" :disabled="!$hasPermission('credit_requests.edit')" @change="validate() && save()" />
                    <has-error :form="form" field="status"/>
                </td>
                <td>
                    <CreditPicker v-model="bank_decision" :columns="decision_columns" :title="bank.name + ' · Решение банка'" :aria-label="bank.name + ' — Решение банка'"
                                  placeholder="Не задано" :width="200" :has-error="form.errors.has('bank_decision')"
                                  :disabled="!showBankDecision" @change="validate() && save()" />
                    <has-error :form="form" field="bank_decision"/>
                </td>
                <td class="crb-row__actions">
                    <el-popover v-if="actions_available" v-model="actions_visible" trigger="click" placement="bottom-end" :width="220" popper-class="crb-actions__popper">
                        <div class="crb-actions" role="menu" :aria-label="'Действия по банку ' + bank.name">
                            <div class="crb-actions__title">{{bank.name}}</div>
                            <button v-if="$hasPermission('credit_requests.edit')" type="button" role="menuitem" class="crb-actions__item" @click="startComment">Комментарий</button>
                            <button v-if="$hasPermission('banks.agreements.generate') && allowedDownloadAgreement" type="button" role="menuitem" class="crb-actions__item" :disabled="downloading" @click="downloadAgreement">Скачать соглашение</button>
                            <button v-if="$hasPermission('credit_requests.delete')" type="button" role="menuitem" class="crb-actions__item crb-actions__item--delete" @click="remove">Удалить заявку</button>
                        </div>
                        <button slot="reference" type="button" class="crb-more" :aria-label="'Действия по банку ' + bank.name" aria-haspopup="menu" :aria-expanded="actions_visible ? 'true' : 'false'">⋮</button>
                    </el-popover>
                </td>
            </template>
            <template v-else>
                <td><span :class="removed ? 'crb-removed' : 'crb-muted'">{{removed ? 'Заявка удалена' : 'Заявка не добавлена'}}</span></td>
                <td>
                    <template v-if="$hasPermission('credit_requests.edit')">
                        <button v-if="removed" type="button" class="crb-add" @click="restore">Отменить удаление</button>
                        <button v-else type="button" class="crb-add" @click="newRequest"><span aria-hidden="true">+</span>Добавить заявку</button>
                    </template>
                </td>
                <td><span class="crb-muted" aria-label="Решение не задано">—</span></td>
                <td></td>
            </template>
        </tr>
        <tr v-if="show_comment_row" class="crb-row__comment">
            <td colspan="5">
                <div v-if="comment_edit_show" class="crb-comment-editor">
                    <label :for="'crb-comment-' + _uid">Комментарий к заявке · {{bank.name}}</label>
                    <textarea
                        :id="'crb-comment-' + _uid"
                        ref="comment"
                        v-model="comment"
                        rows="2"
                        placeholder="Добавьте важные детали по этому банку"
                        @focusout="finishComment"
                        @keydown.enter="finishComment"
                        @keydown.esc.stop.prevent="finishComment"></textarea>
                    <div class="crb-comment-editor__tools">
                        <button type="button" class="crb-button" @click="finishComment">Свернуть</button>
                    </div>
                </div>
                <button v-else type="button" class="crb-comment" :disabled="!$hasPermission('credit_requests.edit')" :aria-label="'Изменить комментарий к банку ' + bank.name" @click="startComment">
                    <span class="crb-comment__icon" aria-hidden="true"></span><span class="crb-comment__text">{{comment}}</span>
                </button>
            </td>
        </tr>
    </tbody>
</template>

<script>
    import Form from "vform";
    import CreditRequestStatusData from "../../../plugins/credit_request_status_data";
    import toastr from "toastr";
    import RoleData from "../../../plugins/role_data";
    import BankData from "../../../plugins/bank_data";
    import CreditPicker from "../../credit_requests/CreditPicker";
    import CreditRequestStatusPicker from "../../credit_requests/CreditRequestStatusPicker";

    const BUS_EVENTS = [
        'credit-request:created', 'credit-request:updated', 'credit-request:deleted',
        'credit-request:created-websocket', 'credit-request:updated-websocket', 'credit-request:deleted-websocket',
    ];
    // Поля, которые пользователь правит в строке
    const EDITABLE_FIELDS = ['credit_manager_id', 'status', 'bank_decision', 'comment'];

    export default {
        name: "CreditRequestItem",
        components: {CreditPicker, CreditRequestStatusPicker},
        props: ['credit_request_model', 'bank', 'managers_select', 'request_id', 'request', 'credit_type', 'origin'],
        data: () => ({
            credit_request: null,
            credit_manager_id: null,
            status: null,
            bank_decision: null,
            comment: null,

            form: new Form(),
            saving: false,
            pending_save: false,
            // Несохранённую заявку удалили, пока шёл её POST: удалим созданную по ответу
            pending_remove: false,
            downloading: false,
            comment_edit_show: false,
            comment_timer: null,
            actions_visible: false,
            // Поля удалённой заявки для «Отменить удаление»
            removed: null,

            bank_decision_select: [{id: "approved", text: "Одобрен"},{id: "rejection", text: "Отказ"},{id: "limit", text: "Лимит"},]
        }),
        mounted() {
            let self = this;
            if (this.credit_request_model) {
                this.loadRequest(this.credit_request_model);
                self.$store.dispatch('credit_requests/update', {
                    bank_id: this.credit_request_model.bank_id,
                    credit_type: this.credit_request_model.credit_type,
                    request: this.credit_request_model,
                });
            }
            this.busHandler = function (EventCreditRequest) {
                // Если событие по нужной кредитной завке
                if (EventCreditRequest.request_id === self.request_id && EventCreditRequest.bank_id === self.bank.id) {
                    // Подгружаем если есть заявка
                    if (EventCreditRequest.credit_request) {
                        self.loadRequest(EventCreditRequest.credit_request);
                    } else {
                        // Очищаем если заявки нет
                        self.unload();
                    }
                }
            };
            this.$bus.$on(BUS_EVENTS, this.busHandler);
        },
        beforeDestroy() {
            this.$bus.$off(BUS_EVENTS, this.busHandler);
            // Закрытие окна не теряет набранный комментарий
            if (this.comment_timer) this.flushComment();
        },
        watch: {
            credit_manager_id: function (val) {
                this.validate();
            },
            status: function (val) {
                this.validate();
            }
        },
        computed: {
            statuses_select() {
                let statuses = CreditRequestStatusData.forSelect();
                let user = this.$store.getters['auth/user'];
                statuses = statuses.filter(s => {
                    if (this.status === s.id) return true;
                    if (s.id === 'oformlen' && this.$store.getters['credit_requests/isset_current_requests_issued']) return false;
                    return RoleData.hasPermissionStatus(user, 'credit_request_status', s.id)
                });
                return statuses;
            },
            status_options() {
                return this.statuses_select.map(s => s.id);
            },
            managers_columns() {
                return [[{key: 'managers', label: '', options: (this.managers_select || []).map(m => ({value: m.id, label: m.text}))}]];
            },
            current_manager_name() {
                let manager = this.credit_request && this.credit_request.credit_manager;
                return manager && manager.id === this.credit_manager_id ? manager.name : '';
            },
            decision_columns() {
                return [[{key: 'decision', label: '', options: this.bank_decision_select.map(d => ({
                    value: d.id,
                    label: d.text,
                    markerClass: 'badge-request-bank-decision-' + d.id,
                    markerText: '',
                }))}]];
            },
            bank_logo() {
                return BankData.logoUrl(this.bank.slug);
            },
            bank_logo_round() {
                return BankData.isRoundLogo(this.bank.slug);
            },
            show_comment_row() {
                return !!this.credit_request && (this.comment_edit_show || !!this.comment);
            },
            actions_available() {
                return this.$hasPermission('credit_requests.edit')
                    || this.$hasPermission('credit_requests.delete')
                    || (this.$hasPermission('banks.agreements.generate') && this.allowedDownloadAgreement);
            },
            allowedDownloadAgreement() {
                return ['plus', 'baltinvest', 'primsoc', 'pochta', 'orange', 'driveclick', 'zenit', 'expo', 'locko', 'soyuz', 'sovcom', 'rusnarbank', 'absolut', 'bystrobank', 'rosbank', 'vologzhanin', 'atb', 'otpbank', 'vtb', 'tinkoff', 'rosgosstrah', 'otkrytie', 'crediteurope', 'alfabank', 'freedom-finance', 'uralsib', 'gazprombank', 'broxi', 'mbrusbank', 'ingobank', 'sber', 'sngb', 'psbank', 'levoberezhny', 'mts', 'renessans', 'energobank', 'appruvo', 'europlan', 'reso', 'autofinancebank', 'royalcreditbank'].indexOf(this.bank.slug) !== -1;
            },
            showBankDecision() {
                return this.$hasRole('admin');
            },
        },
        methods: {
            loadRequest(request) {
                this.$store.dispatch('credit_requests/update', {
                    bank_id: request.bank_id,
                    credit_type: request.credit_type,
                    request: request,
                });

                this.credit_request = request;
                this.credit_manager_id = request.credit_manager_id;
                this.status = request.status;
                this.bank_decision = request.bank_decision;
                this.comment = request.comment;
                this.removed = null;
            },
            // Ответ сервера применяем только к полям, которые не менялись после отправки запроса:
            // успех старого запроса не затирает более новые правки
            applyResponse(request, sent) {
                this.$store.dispatch('credit_requests/update', {
                    bank_id: request.bank_id,
                    credit_type: request.credit_type,
                    request: request,
                });
                this.credit_request = request;
                EDITABLE_FIELDS.forEach(field => {
                    if (this[field] === sent[field]) this[field] = request[field];
                });
            },
            find() {
                let items = this.$store.getters['credit_requests/current_requests'];
                let obj = items[this.bank.id + "_" + this.credit_type];
                return obj && obj.id ? obj : null;
            },
            newRequest() {
                let user_id = this.$store.getters['auth/user'].id;
                let credit_manager_id = null;
                if (_.find(this.managers_select, ['id', user_id])) credit_manager_id = user_id;

                let request = {
                    request_id: this.request_id,
                    bank_id: this.bank.id,
                    credit_type: this.credit_type,
                    credit_manager_id: credit_manager_id,
                    status: "chernovik",
                    comment: null,
                };
                this.loadRequest(request);
                if (this.validate()) this.save();
            },
            validate() {
                this.form = new Form();

                let data_save = {};

                data_save.request_id = this.request_id;
                data_save.bank_id = this.bank.id;
                data_save.credit_type = this.credit_type;
                data_save.status = this.status ? this.status : null;
                data_save.comment = this.comment;
                if (this.showBankDecision) data_save.bank_decision = this.bank_decision;

                if (!this.credit_manager_id) {
                    this.form.errors.set('credit_manager_id', ['Выберите менеджера']);
                } else {
                    data_save.credit_manager_id = this.credit_manager_id;
                }

                if (this.form.errors.any()) {
                    return false;
                } else {
                    this.form = new Form(data_save);
                    return true;
                }
            },
            // Запросы идут по очереди: правка во время запроса сохраняется следующим запросом
            save() {
                if (this.saving) {
                    this.pending_save = true;
                    return;
                }
                if (!this.credit_request.id) {
                    let find = this.find();
                    if (find) {
                        toastr.error('Заявка в этот банк уже существует!');
                        this.loadRequest(find);
                    } else {
                        this.send('post', 'credit-request:created');
                    }
                } else {
                    this.send('put', 'credit-request:updated');
                }
            },
            send(method, event) {
                let self = this;
                let sent = _.pick(this, EDITABLE_FIELDS);
                this.saving = true;
                this.form[method]('/api/credit_requests')
                    .then(function (response) {
                        self.saving = false;
                        if (self.pending_remove) {
                            self.pending_remove = false;
                            self.destroy(response.data.id);
                            self.savePending();
                            return;
                        }
                        self.applyResponse(response.data, sent);
                        self.$bus.$emit(event, {
                            request_id: self.request_id,
                            bank_id: self.bank.id,
                            credit_type: self.credit_type,
                            credit_request: response.data,
                        });
                        self.savePending();
                    })
                    .catch(function () {
                        // Введённые данные остаются в строке, следующая правка отправит их снова
                        self.saving = false;
                        if (self.pending_remove) {
                            self.pending_remove = false;
                            self.savePending();
                            return;
                        }
                        self.$message.error({message: 'Не удалось сохранить заявку в ' + self.bank.name, duration: 3000});
                        self.savePending();
                    });
            },
            savePending() {
                if (!this.pending_save || !this.credit_request) return;
                this.pending_save = false;
                if (this.validate()) this.save();
            },
            startComment() {
                this.actions_visible = false;
                this.comment_edit_show = true;
                this.$nextTick(() => this.$refs.comment && this.$refs.comment.focus());
            },
            flushComment() {
                clearTimeout(this.comment_timer);
                this.comment_timer = null;
                if (!this.credit_request) return;
                let saved = (this.credit_request.comment || '');
                if ((this.comment || '') === saved) return;
                if (this.validate()) this.save();
            },
            finishComment() {
                this.flushComment();
                this.comment_edit_show = false;
            },
            unload() {
                this.$store.dispatch('credit_requests/remove', {
                    bank_id: this.bank.id,
                    credit_type: this.credit_type,
                });

                clearTimeout(this.comment_timer);
                this.comment_timer = null;
                this.comment_edit_show = false;
                this.credit_request = null;
                this.credit_manager_id = null;
                this.status = null;
                this.bank_decision = null;
                this.comment = null;
            },
            remove() {
                let self = this;
                let snapshot = _.pick(this, EDITABLE_FIELDS);
                this.actions_visible = false;

                // Заявка ещё не сохранена: на сервере удалять нечего, просто убираем строку
                if (!this.credit_request.id) {
                    if (this.saving) this.pending_remove = true;
                    this.pending_save = false;
                    this.unload();
                    return;
                }

                this.destroy(this.credit_request.id)
                    .then(function (deleted) {
                        if (!deleted) return;
                        self.unload();
                        self.removed = snapshot;
                    });
            },
            destroy(id) {
                let self = this;
                return axios.delete('/api/credit_requests/' + id)
                    .then(function () {
                        self.$bus.$emit('credit-request:deleted', {
                            request_id: self.request_id,
                            bank_id: self.bank.id,
                            credit_type: self.credit_type,
                        });
                        return true;
                    }, function () {
                        self.$message.error({message: 'Не удалось удалить заявку в ' + self.bank.name, duration: 3000});
                        return false;
                    });
            },
            // Восстановление — новая заявка с полями удалённой
            restore() {
                let snapshot = this.removed;
                this.loadRequest(Object.assign({
                    request_id: this.request_id,
                    bank_id: this.bank.id,
                    credit_type: this.credit_type,
                }, snapshot));
                if (this.validate()) this.save();
            },

            downloadAgreement() {
                let self = this;
                this.actions_visible = false;
                if (!this.request.profiles || !this.request.profiles.length) {
                    this.$message.error({
                        message: 'Создайте анкету',
                        duration: 2000,
                    });
                } else {
                    this.downloading = true;
                    axios.get('/api/banks/agreements/generate/' + this.request_id + '/' + this.bank.id, {responseType: 'arraybuffer'})
                        .then(function (response) {
                            self.downloading = false;
                            let content_disposition = response.headers['content-disposition'];
                            let ext = 'xlsx';
                            if (content_disposition.indexOf('.docx') !== -1) ext = 'docx';
                            const link = document.createElement('a');
                            link.href = window.URL.createObjectURL(new Blob([response.data]));
                            link.setAttribute('download', 'agreement_' + self.bank.slug + "_" + self.request_id + "_" + (new Date).getTime() + '.' + ext);
                            document.body.appendChild(link);
                            link.click()
                        })
                        .catch(function (e) {
                            self.downloading = false;
                            self.$message.error({
                                message: 'Не удалось сгенерировать соглашение',
                                duration: 2000,
                            });
                        })
                }
            },
        },
    }
</script>

<style scoped>
    .crb-row td {
        height: 51px;
        padding: 8px 12px;
        border-bottom: 1px solid #e9ecef;
        vertical-align: middle;
        background: #fff;
    }
    .crb-row td:first-child {
        padding-left: var(--crb-gutter, 12px);
    }
    .crb-row td:last-child {
        padding-left: 0;
        padding-right: 12px;
    }
    .crb-row:hover td {
        background: #f7f9fb;
    }
    .crb-row.has-comment .crb-row__main td {
        border-bottom: 0;
    }
    .crb-row.is-origin td {
        background: #f0f5ff;
    }
    .crb-row.is-origin td:first-child {
        box-shadow: inset 3px 0 #2c7be5;
    }
    .crb-bank {
        display: flex;
        align-items: center;
        gap: 11px;
        min-width: 0;
    }
    .crb-bank__logo {
        flex: 0 0 26px;
        width: 26px;
        height: 26px;
        padding: 2px;
        object-fit: contain;
        background: #fff;
    }
    .crb-bank__name {
        min-width: 0;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.3;
        color: #3F4B57;
    }
    .crb-muted {
        color: #6c757d;
        font-size: 12px;
    }
    .crb-removed {
        color: #b42b40;
        font-size: 12px;
    }
    .crb-add {
        padding: 5px 0;
        border: 0;
        background: transparent;
        color: #2c7be5;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        cursor: pointer;
    }
    .crb-add:hover {
        text-decoration: underline;
        color: #1657af;
    }
    .crb-add span {
        margin-right: 5px;
        font-size: 16px;
        font-weight: 400;
        vertical-align: -1px;
    }
    .crb-more {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 32px;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: #6c757d;
        font-size: 22px;
        line-height: 1;
        cursor: pointer;
    }
    .crb-more:hover,
    .crb-more[aria-expanded="true"] {
        background: #f0f5ff;
        color: #2c7be5;
    }
    .crb-actions__title {
        padding: 5px 10px 8px;
        margin-bottom: 4px;
        border-bottom: 1px solid #e9ecef;
        color: #6c757d;
        font-size: 11px;
        font-weight: 600;
    }
    .crb-actions__item {
        display: block;
        width: 100%;
        min-height: 35px;
        padding: 8px 10px;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: #3F4B57;
        font-size: 13px;
        text-align: left;
        cursor: pointer;
    }
    .crb-actions__item:hover,
    .crb-actions__item:focus-visible {
        background: #f0f5ff;
        outline: none;
    }
    .crb-actions__item:disabled {
        opacity: .5;
        cursor: default;
    }
    .crb-actions__item--delete {
        margin-top: 4px;
        border-top: 1px solid #e9ecef;
        border-radius: 0 0 6px 6px;
        color: #b42b40;
    }
    /* Ячейка комментария одновременно первая и последняя — отступы задаём явно, текст начинается под названием банка */
    .crb-row .crb-row__comment td {
        height: auto;
        padding: 0 var(--crb-gutter, 12px) 11px calc(var(--crb-gutter, 12px) + 37px);
    }
    .crb-comment {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        max-width: 100%;
        padding: 0;
        border: 0;
        background: transparent;
        color: #6c757d;
        font-size: 12px;
        line-height: 1.5;
        text-align: left;
        cursor: pointer;
    }
    .crb-comment:disabled {
        cursor: default;
    }
    .crb-comment__text {
        white-space: pre-wrap;
        overflow-wrap: anywhere;
    }
    .crb-comment__icon {
        position: relative;
        flex: 0 0 13px;
        width: 13px;
        height: 10px;
        margin-top: 4px;
        border: 1.3px solid currentColor;
        border-radius: 4px;
    }
    .crb-comment__icon:after {
        content: "";
        position: absolute;
        left: 2px;
        bottom: -4px;
        width: 4px;
        height: 4px;
        border-left: 1.3px solid currentColor;
        transform: skewY(-38deg);
    }
    .crb-comment-editor {
        padding-top: 5px;
    }
    .crb-comment-editor label {
        display: block;
        margin-bottom: 6px;
        color: #6c757d;
        font-size: 11px;
    }
    .crb-comment-editor textarea {
        display: block;
        width: 100%;
        min-height: 64px;
        padding: 9px 11px;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        color: #3F4B57;
        font-size: 13px;
        line-height: 1.5;
        resize: vertical;
    }
    .crb-comment-editor textarea:focus {
        border-color: #2c7be5;
        outline: none;
        box-shadow: 0 0 0 2px rgba(44, 123, 229, .15);
    }
    .crb-comment-editor__tools {
        display: flex;
        justify-content: flex-end;
        margin-top: 7px;
    }
    .crb-button {
        height: 30px;
        padding: 0 14px;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        background: #fff;
        color: #3F4B57;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
    }
    .crb-button:hover {
        background: #f7f9fb;
    }
</style>

<style>
    .crb-actions__popper.el-popover {
        padding: 6px;
        border-radius: 10px;
    }
</style>
